import type { ReactElement } from 'react';

import {
  businessDayOf,
  dayOfDateOnly,
  EMPTY_DATE,
  formatDate,
  formatDateTime,
  formatInstantDate,
  formatTime,
} from '@/lib/time';

// Presentación de fechas y horas con el formato canónico del módulo de tiempo
// (dd/mm/aaaa, 7:00 am, zona de negocio). Renderiza <time dateTime> para lectores de
// pantalla y herramientas; un valor vacío o inválido muestra "—" sin <time>.

interface BaseProps {
  value: string | null;
  className?: string;
}

function Time({
  text,
  dateTime,
  className,
}: {
  text: string;
  dateTime: string | null;
  className?: string;
}): ReactElement {
  return dateTime === null || text === EMPTY_DATE ? (
    <span className={className}>{EMPTY_DATE}</span>
  ) : (
    <time dateTime={dateTime} className={className}>
      {text}
    </time>
  );
}

/** Fecha: `kind="date"` para una fecha pura (`@db.Date`), `kind="instant"` para el día
 *  de negocio de un instante. */
export function DateText({
  value,
  kind,
  className,
}: BaseProps & { kind: 'date' | 'instant' }): ReactElement {
  if (kind === 'date') {
    return <Time text={formatDate(value)} dateTime={dayOfDateOnly(value)} className={className} />;
  }
  const valid = value !== null && businessDayOf(value) !== null;
  return (
    <Time text={formatInstantDate(value)} dateTime={valid ? value : null} className={className} />
  );
}

/** Fecha y hora de negocio de un instante; `seconds` para la bitácora. */
export function DateTimeText({
  value,
  seconds = false,
  className,
}: BaseProps & { seconds?: boolean }): ReactElement {
  return <Time text={formatDateTime(value, { seconds })} dateTime={value} className={className} />;
}

/** Hora de negocio de un instante. */
export function TimeText({ value, className }: BaseProps): ReactElement {
  return <Time text={formatTime(value)} dateTime={value} className={className} />;
}
